import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EventDashboardApi } from '../../api/eventDashboardApi';
import { ProgramTypesGUID } from '../../models/_enum-collections';
import { ApiResponse } from '../../models/api-response.model';
import {
  EventCountModel,
  PendingReviewCountModel
} from '../../models/event-count-model';
import { EventCustomerDetails } from '../../models/event-customer-details.model';
import { EventMilestone2PaymentDetails } from '../../models/event-milestone-2-payment-details.model';
import {
  EventNoteModel,
  AddEventNoteRequestModel
} from '../../models/event-note.model';
import { EventPartnerModel } from '../../models/event-partner.model';
import {
  EventPaymentInformationModel,
  UpdateEventPaymentInformationRequest
} from '../../models/event-payment-information.model';
import {
  EventRegistrationModel,
  EventRegistrationCustomerModel
} from '../../models/event-registration.model';
import { EventSearchFilterRequest } from '../../models/event-search-filter-request.model';
import { Milestone2InvoiceRequest } from '../../models/milestone2-invoice-request.model';
import { Milestone2PaymentStatus } from '../../models/milestone2-payment-status.model';
import { PagedResult } from '../../models/page-result.model';
import { PayoutMetricOptions } from '../../models/payout-metric-options.model';
import { PoeDocumentModel } from '../../models/poe-document.model';
import { EventTargetProduct } from '../../models/event-target-products.model';

@Injectable({
  providedIn: 'root'
})
export class EventDashboardService {
  constructor(protected eventDashboardApi: EventDashboardApi) {}

  getEventRegistration(
    eventRegistrationId: number
  ): Observable<EventRegistrationModel> {
    return this.eventDashboardApi.getEventRegistration(eventRegistrationId);
  }

  getAllEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAllEvents(request);
  }

  getAwaitingCustomerValidationEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingCustomerValidationEvents(request);
  }

  getAwaitingInvoiceDocReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingInvoiceDocReviewEvents(request);
  }

  getAwaitingCustomerSurveyReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingCustomerSurveyReviewEvents(
      request
    );
  }

  getEventPartnerDetails(
    eventRegistrationId: number
  ): Observable<EventPartnerModel> {
    return this.eventDashboardApi.getEventPartnerDetails(eventRegistrationId);
  }

  updatePartnerDetails(request: EventPartnerModel): Observable<boolean> {
    return this.eventDashboardApi.updatePartnerDetails(request);
  }

  getEventCustomers(
    eventRegistrationId: number
  ): Observable<EventRegistrationCustomerModel[]> {
    return this.eventDashboardApi.getEventCustomers(eventRegistrationId);
  }

  getOneToOneEventCustomerDetails(
    eventRegistrationId: number
  ): Observable<EventCustomerDetails> {
    return this.eventDashboardApi.getOneToOneEventCustomerDetails(
      eventRegistrationId
    );
  }

  updateCustomerDetails(request: EventCustomerDetails): Observable<boolean> {
    return this.eventDashboardApi.updateCustomerDetails(request);
  }

  getEventCount(): Observable<EventCountModel> {
    return this.eventDashboardApi.getEventCount();
  }

  getEngagementsCount(
    programTypeGuid: ProgramTypesGUID
  ): Observable<PendingReviewCountModel> {
    return this.eventDashboardApi.getEngagementsCount(programTypeGuid);
  }

  getPendingReviewQueues(
    programTypeGuid: ProgramTypesGUID
  ): Observable<PendingReviewCountModel> {
    return this.eventDashboardApi.getPendingReviewQueues(programTypeGuid);
  }

  getEventPaymentInformation(
    eventId: number,
    programTypeGuid: string
  ): Observable<EventPaymentInformationModel> {
    return this.eventDashboardApi.getEventPaymentInformation(
      eventId,
      programTypeGuid
    );
  }
  updateEventPaymentInformation(
    request: UpdateEventPaymentInformationRequest
  ): Observable<ApiResponse> {
    return this.eventDashboardApi.updateEventPaymentInformation(request);
  }
  getAwaitingSKUPurchaseReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingSKUPurchaseReviewEvents(request);
  }

  getAwaitingDAPoeReviewEvents(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingDAPoeReviewEvents(request);
  }

  getEventNotes(eventRegistrationId: number): Observable<EventNoteModel[]> {
    return this.eventDashboardApi.getEventNotes(eventRegistrationId);
  }

  addEventNote(
    addEventNoteRequest: AddEventNoteRequestModel
  ): Observable<ApiResponse> {
    return this.eventDashboardApi.addEventNote(addEventNoteRequest);
  }
  getEventsTargetProductList(): Observable<EventTargetProduct[]> {
    return this.eventDashboardApi.getTargetProductsList();
  }

  getEventsMilestone2PaymentDetails(
    eventRegistrationId: number,
    programTypeGuId: string
  ): Observable<EventMilestone2PaymentDetails> {
    return this.eventDashboardApi.getMilestone2PaymentDetails(
      eventRegistrationId,
      programTypeGuId
    );
  }

  getEventsMilestone2PaymentStatuses(): Observable<Milestone2PaymentStatus[]> {
    return this.eventDashboardApi.getMilestone2PaymentStatuses();
  }

  getEventsPayoutMetricOptions(
    eventRegistrationId: number
  ): Observable<PayoutMetricOptions[]> {
    return this.eventDashboardApi.getPayoutMetricOptions(eventRegistrationId);
  }

  updateEventsMilestone2Details(
    request: Milestone2InvoiceRequest
  ): Observable<boolean> {
    return this.eventDashboardApi.updateMilestone2Details(request);
  }

  getPoeDocuments(progrmaTypeGUID: string): Observable<PoeDocumentModel[]> {
    return this.eventDashboardApi.getPoeDocuments(progrmaTypeGUID);
  }

  getProgramTypeGuidFromUrl(): ProgramTypesGUID {
    let programTypeGuid = ProgramTypesGUID.MCAP;
    const url = window.location.href.toLowerCase();

    if (url.indexOf('mssp') > -1) {
      programTypeGuid = ProgramTypesGUID.MSSP;
    } else if (url.indexOf('operator') > -1) {
      programTypeGuid = ProgramTypesGUID.Operator;
    } else if (url.indexOf('events') > -1) {
      programTypeGuid = ProgramTypesGUID.Events;
    } else if (url.indexOf('advisor') > -1) {
      programTypeGuid = ProgramTypesGUID.MSAIP;
    } else if (url.indexOf('mrpp') > -1) {
      programTypeGuid = ProgramTypesGUID.MRPP;
    }

    return programTypeGuid;
  }

  getPOEDocReviewEngagements(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getPOEDocReviewEngagements(request);
  }

  getEngagementsForMrppCustAckList(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getEngagementsForMrppCustAckList(request);
  }
  getAwaitingCustomerSurveyReviewMrpp(
    request: EventSearchFilterRequest
  ): Observable<PagedResult<EventRegistrationModel>> {
    return this.eventDashboardApi.getAwaitingCustomerSurveyReviewMrpp(request);
  }
}
